import { Donut } from "@react-gcc-eds/charts";
import { Serie } from "@react-gcc-eds/charts/dist/components/donut/interfaces";
import { Pill, Tile, useSettings } from "@react-gcc-eds/core";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { DashboardGraph, IDashboardItemSelected } from "..";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { useMetricsFiltersActions } from "../../../../actions/metrics-filters-actions";
import { useSettingsActions } from "../../../../actions/settings-actions";
import { useStore } from "../../../../store";
import { dateAsYMDString } from "../../../../utils";
import { useLocalization } from "../../../../utils/localization";
import DatePicker from "../../../filters/date-picker";
import { usePredefinedRanges } from "../../../filters/date-picker/predefined-ranges";
import CustomTabs, { ITab } from "../../../shared/custom-tabs";
import { DashboardTooltipHeader } from "../../../shared/tooltip-header";
import { usePoStatus } from "../../helpers/po-status";
import { DashboardTileAlignment as TileAlignment } from "../index";
import { NoMetrics, noStatusMetric } from "../utils";

const OverallOrderStatusDonut = ({
  labels,
  customerId,
  className,
  alignment,
  onItemSelected
}: {
  labels: { [key: string]: string | undefined };
  customerId: string;
  className: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const [state] = useStore();
  const fetching =
    state.fetchingData.metrics.orderCreationDateDonutMetric &&
    state.fetchingData.metrics.itemCreationDateDonutMetric;
  const tileRef = useRef<HTMLDivElement>(null);
  const { currentCustomer } = useSettingsActions();
  const [hiddenOrdersStatuses, setHiddenOrdersStatuses] = useState([]);
  const [hiddenItemsStatuses, setHiddenItemsStatuses] = useState([]);
  const [tabs, setTabs] = useState<ITab[]>([]);
  const { localeString } = useLocalization();
  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);

  const { getOrderCreationDateDonutMetric, getItemCreationDateDonutMetric } = useMetricsActions();
  const { setMetricsOverallOrderStatusDonutCreationRange } = useMetricsFiltersActions();

  const { labelToStatus } = usePoStatus();
  const { rangeAsString, largestRange, isLargestRange } = usePredefinedRanges(
    new Date(currentCustomer().dataAvailableFromDate)
  );
  const { theme } = useSettings();

  useEffect(() => {
    if (state.metricsFilters.overallOrderStatusDonutCreationRange) {
      const { from, to } = state.metricsFilters.overallOrderStatusDonutCreationRange;

      const rangeFrom = dateAsYMDString(from);
      const rangeTo = dateAsYMDString(to);

      getOrderCreationDateDonutMetric(rangeFrom, rangeTo);
      getItemCreationDateDonutMetric(rangeFrom, rangeTo);
    }
  }, [state.metricsFilters.overallOrderStatusDonutCreationRange, customerId]);

  const purchaseOrders = () => {
    const { orderCreationDateDonutMetric } = state.metrics;
    const { orderCreationDateDonutMetric: loadingOrder } = state.fetchingData.metrics;

    if (loadingOrder) {
      return <NoMetrics loading />;
    }
    if (noStatusMetric(orderCreationDateDonutMetric)) {
      return <NoMetrics />;
    }
    const d: Serie[] = [];
    orderCreationDateDonutMetric.forEach(m => {
      d.push({
        colorID: m.status,
        name: m.label,
        values: [m.totalCount],
        hidden: hiddenOrdersStatuses.find(i => i === m.status) !== undefined
      });
    });

    return (
      <Donut
        onClick={(label: string) => {
          if (onItemSelected) {
            onItemSelected({
              filters: {
                orderCreationRange: state.metricsFilters.overallOrderStatusDonutCreationRange,
                status: labelToStatus(label)
              },
              graph: DashboardGraph.Orders
            });
          }
        }}
        data={{ series: d }}
        unit="orders"
        showValue
        showAbsoluteValue
        onToggle={(data: any) =>
          setHiddenOrdersStatuses(
            data.series.filter((d: Serie) => d.hidden).map((d: Serie) => d.colorID)
          )
        }
      />
    );
  };

  const purchaseOrdersItems = () => {
    const { itemCreationDateDonutMetric } = state.metrics;
    const { itemCreationDateDonutMetric: loading } = state.fetchingData.metrics;

    if (loading) {
      return <NoMetrics loading />;
    }
    if (noStatusMetric(itemCreationDateDonutMetric)) {
      return <NoMetrics />;
    }

    const d: Serie[] = [];
    itemCreationDateDonutMetric.forEach(m => {
      d.push({
        colorID: m.status,
        name: m.label,
        values: [m.totalCount],
        hidden: hiddenItemsStatuses.find(i => i === m.status) !== undefined
      });
    });

    return (
      <Donut
        onClick={(label: string) => {
          if (onItemSelected) {
            onItemSelected({
              filters: {
                orderCreationRange: state.metricsFilters.overallOrderStatusDonutCreationRange,
                status: labelToStatus(label)
              },
              graph: DashboardGraph.Items
            });
          }
        }}
        data={{ series: d }}
        unit="order items"
        showAbsoluteValue
        showValue
        onToggle={(data: any) =>
          setHiddenItemsStatuses(
            data.series.filter((d: Serie) => d.hidden).map((d: Serie) => d.colorID)
          )
        }
      />
    );
  };

  const resetFilter = () => {
    setMetricsOverallOrderStatusDonutCreationRange(largestRange.selector());
  };

  const resetFilterPill = () => {
    return (
      <Pill className={cx("filter-pill", theme)} onToggle={resetFilter}>
        Reset filters
      </Pill>
    );
  };

  const filtersSummary = (
    <div className="dashboard-active-filter">
      <Pill>{rangeAsString(state.metricsFilters.overallOrderStatusDonutCreationRange)}</Pill>
      {!isLargestRange(state.metricsFilters.overallOrderStatusDonutCreationRange)
        ? resetFilterPill()
        : null}
    </div>
  );

  const dateFilter = (
    <DatePicker
      key="datepicker"
      dual={state.userPreferences.useDualCalendar}
      rolling
      right={alignment !== TileAlignment.LEFT}
      selectedRange={state.metricsFilters.overallOrderStatusDonutCreationRange}
      oldestDate={new Date(currentCustomer().dataAvailableFromDate)}
      onRangeSelected={setMetricsOverallOrderStatusDonutCreationRange}
    />
  );

  const tileHeader = (
    <div className="dashboard-tile">
      <DashboardTooltipHeader
        tooltipInfo="Graph is showing the status for the selected time period"
        alignment={alignment}
        header={localeString("Dashboard_OverallOrderStatusPieChart") || "Overall order status"}
      />
    </div>
  );

  const purchaseOrderTab = {
    view: purchaseOrders(),
    title: labels["purchaseOrders"],
    index: 0
  } as ITab;

  const purchaseOrderItemsTab = {
    view: purchaseOrdersItems(),
    title: labels["purchaseOrderItems"],
    index: 1
  } as ITab;

  useEffect(() => {
    const tabs = [];
    if (labels["purchaseOrders"] !== undefined) {
      tabs.push(purchaseOrderTab);
    }
    if (labels["purchaseOrderItems"] !== undefined) {
      tabs.push(purchaseOrderItemsTab);
    }
    setTabs(tabs);
  }, [
    state.fetchingData.metrics.orderCreationDateDonutMetric,
    state.fetchingData.metrics.itemCreationDateDonutMetric,
    hiddenItemsStatuses,
    hiddenOrdersStatuses
  ]);

  return (
    <>
      <Tile
        className={className}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        title={tileHeader}
        subtitle={filtersSummary}
        actions={[dateFilter]}
      >
        <CustomTabs tabs={tabs} />
      </Tile>
    </>
  );
};

export default OverallOrderStatusDonut;
